/**
 * Maintenance log rules that need no database: the repair signature (data_hash), what a
 * partial update does to it, which logs are true duplicates, and what a Date IN edit writes.
 */
import { normalizePlate } from './possessionMatcher';

/** The signature of one repair: plate | damage | Date OUT (stored as data_hash, used by addLog to find it again). */
export const maintenanceHash = (log) => {
    const vehicle = (log?.Vehicle || '').toString().trim().toLowerCase();
    const damage = (log?.['Damage Details'] || '').toString().trim();
    const dateOut = (log?.['Date OUT'] || '').toString().trim();
    return `${vehicle}|${damage}|${dateOut}`;
};

export const SIGNATURE_FIELDS = ['Vehicle', 'Damage Details', 'Date OUT'];

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj || {}, key);

/** True when an update changes part of the signature (so data_hash must be recomputed). */
export const touchesSignature = (data) => SIGNATURE_FIELDS.some((k) => has(data, k));

/** True when the update alone is not enough to rebuild the signature: the saved log must be read first. */
export const needsSavedLog = (data) => touchesSignature(data) && !SIGNATURE_FIELDS.every((k) => has(data, k));

/**
 * data_hash to write with an update, from the log as it will be AFTER the merge; undefined = leave
 * the stored one alone. A Date IN edit sends only {Date IN, Status}: hashing that alone gave "||"
 * and addLog then saved the same repair a second time (audit FLT-10).
 */
export const hashAfterUpdate = (saved, data) => (touchesSignature(data) ? maintenanceHash({ ...(saved || {}), ...data }) : undefined);

// ---- true duplicates -------------------------------------------------------------------------

const clean = (v) => String(v ?? '').replace(/\r/g, '').trim().replace(/\s+/g, ' ').toLowerCase();

/** A field by name, also under the legacy spellings ("Status\r", "date out"). */
const fieldOf = (row, name) => {
    const want = name.toLowerCase();
    const key = Object.keys(row || {}).find((k) => k.replace(/\r/g, '').trim().toLowerCase() === want);
    return key ? row[key] : '';
};

/** One day in one form: "2026-09-05", "05/09/2026", "5-9-26" -> "5/9/2026"; other text as it is. */
export const dayKey = (value) => {
    const t = clean(value);
    let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(t);
    if (m) return `${Number(m[3])}/${Number(m[2])}/${m[1]}`;
    m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/.exec(t);
    if (m) {
        const year = m[3].length === 2 ? `${Number(m[3]) < 50 ? '20' : '19'}${m[3]}` : m[3];
        return `${Number(m[1])}/${Number(m[2])}/${year}`;
    }
    return t;
};

const hasDateIn = (row) => clean(fieldOf(row, 'Date IN')) !== '';

/**
 * Groups of logs that are the SAME repair entered more than once: same car AND same Date OUT AND
 * the same damage text. Before, logs were grouped by plate alone, so two different open repairs of
 * one car ("Oil change" and "Accident front bumper") counted as duplicates and one was deleted
 * (audit FLT-06). A log missing any of the three is never treated as a duplicate.
 * Each group: { key, plate, dateOut, damage, keep, remove: [..] } - the log kept is the one with a
 * Date IN (the finished record), then the most recently updated.
 */
export const trueDuplicateGroups = (logs) => {
    const groups = new Map();
    (logs || []).forEach((row) => {
        const plate = normalizePlate(fieldOf(row, 'Vehicle'));
        const dateOut = dayKey(fieldOf(row, 'Date OUT'));
        const damage = clean(fieldOf(row, 'Damage Details'));
        if (!plate || !dateOut || !damage) return;
        const key = `${plate}|${dateOut}|${damage}`;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(row);
    });
    return [...groups.entries()]
        .filter(([, list]) => list.length > 1)
        .map(([key, list]) => {
            const ranked = [...list].sort((a, b) => (Number(hasDateIn(b)) - Number(hasDateIn(a)))
                || String(b.last_updated || '').localeCompare(String(a.last_updated || '')));
            const first = ranked[0];
            return {
                key,
                plate: String(fieldOf(first, 'Vehicle') || '').trim(),
                dateOut: String(fieldOf(first, 'Date OUT') || '').trim(),
                damage: String(fieldOf(first, 'Damage Details') || '').trim(),
                keep: first,
                remove: ranked.slice(1),
            };
        });
};

// ---- Date IN edit ----------------------------------------------------------------------------

/**
 * The fields a Date IN edit writes. `raw` is the date input's value ("yyyy-mm-dd", "" when cleared).
 * null = not a whole date yet (a year typed digit by digit: 0002, 0020, 0202), nothing is written.
 * A date closes the log (Completed); clearing it opens the log again (Pending, as a new log
 * without Date IN) instead of leaving "Completed" on an open log (audit FLT-16).
 */
/**
 * The warning on an OPEN log (no Date IN) whose car the fleet - Ejar's status, merged into fleet/<plate> -
 * shows Ready or Rented: the car is most likely back and the log was never closed (the engine does not close
 * a log by itself). `fleetStatus` is the fleet doc's `status`; null = no warning. Owner, 26 Sep 2026.
 */
const BACK_IN_SERVICE = { ready: 'Ready', rented: 'Rented' };
export const openLogFleetWarning = (log, fleetStatus) => {
    if (!log) return null;
    const dateInKey = Object.keys(log).find((k) => k.toLowerCase().includes('date in'));
    if (dateInKey && String(log[dateInKey] ?? '').trim()) return null;
    const shown = BACK_IN_SERVICE[String(fleetStatus ?? '').trim().toLowerCase()];
    return shown ? `Ejar shows this car ${shown} - close the log if it is back` : null;
};

export const dateInUpdate = (raw) => {
    const value = String(raw ?? '').trim();
    if (!value) return { 'Date IN': '', Status: 'Pending' };
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return null;
    const year = Number(m[1]);
    if (year < 2000 || year > 2099) return null;
    return { 'Date IN': `${m[3]}/${m[2]}/${m[1]}`, Status: 'Completed' };
};
